import { Component } from '@theme/component';
import { imageUrl } from '@theme/utilities';

/**
 * @typedef {Object} ProductStickyBarRefs
 * @property {HTMLElement} [price]
 * @property {HTMLElement} [compare]
 * @property {HTMLElement} [thumbs]
 * @property {HTMLButtonElement} [add]
 */

/**
 * @typedef {Object} StickyThumb
 * @property {string} url Product URL
 * @property {string} image Featured image URL (without width)
 * @property {string} title Product title, used as text only
 */

/**
 * Product page quick add bar. It lives in <body> (no ancestor can trap the fixed layer) and shows
 * once the element whose id is in `data-anchor` (the main add button) has scrolled above the viewport.
 * The product page drives it through `setPrice()` and `addThumbs()`; its add button targets the
 * product page with `on:click="#<product-page id>/addFromStickyBar"`.
 *
 * @extends {Component<ProductStickyBarRefs>}
 */
export class ProductStickyBar extends Component {
  /** @type {boolean | null} */
  #shown = null;

  connectedCallback() {
    if (this.parentElement !== document.body) {
      // Theme editor re-renders leave the previous bar in <body>: drop it before taking its place
      for (const stale of document.querySelectorAll(`product-sticky-bar[id="${this.id}"]`)) {
        if (stale !== this) stale.remove();
      }
      // Reconnects right away: initialisation runs on that second connection
      document.body.appendChild(this);
      return;
    }
    super.connectedCallback();
    this.#shown = null;
    window.addEventListener('scroll', this.#update, { passive: true });
    window.addEventListener('resize', this.#update, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#update);
    window.removeEventListener('resize', this.#update);
  }

  /**
   * @param {string} price Formatted price
   * @param {string | null} compare Formatted compare-at price, null when not on sale
   */
  setPrice(price, compare) {
    const { price: priceElement, compare: compareElement } = this.refs;
    if (priceElement) priceElement.textContent = price;
    if (compareElement) {
      compareElement.hidden = !compare;
      compareElement.textContent = compare || '';
    }
  }

  /**
   * Repeats the colour thumbnails (Figma), linking to the sibling products.
   * @param {StickyThumb[]} items
   */
  addThumbs(items) {
    const thumbs = this.refs.thumbs;
    if (!thumbs) return;
    for (const item of items) {
      const link = document.createElement('a');
      link.className = 'product-page__sticky-thumb';
      link.href = item.url;
      link.tabIndex = -1;
      const img = document.createElement('img');
      img.src = imageUrl(item.image, 80);
      img.alt = item.title;
      img.loading = 'lazy';
      const label = document.createElement('span');
      label.className = 'screen-reader-only';
      label.textContent = item.title; // titles come from the catalogue sync: never as HTML
      link.append(img, label);
      thumbs.appendChild(link);
    }
  }

  /** One rect read per scroll */
  #update = () => {
    const anchor = document.getElementById(this.dataset.anchor || '');
    if (!anchor) return;
    const show = anchor.getBoundingClientRect().bottom < 0;
    if (show === this.#shown) return;
    this.#shown = show;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', String(!show));
    if (this.refs.add) this.refs.add.tabIndex = show ? 0 : -1;
  };
}

if (!customElements.get('product-sticky-bar')) {
  customElements.define('product-sticky-bar', ProductStickyBar);
}
